"use client";

import { useEffect, useState } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import PlaygroundTable from "@/components/playground/PlaygroundTable";
import PlaygroundBottomNav from "@/components/playground/PlaygroundBottomNav";
import SearchBar from "@/components/dashboard/SearchBar";
import CountyStateFilter from "@/components/dashboard/CountyStateFilter";
import type { AddressData } from "@/types/address";
import { Loader2 } from "lucide-react";
import {
	createCheckoutSessionWithAuth,
	STRIPE_PRO_PRICE_ID,
} from "@/services/stripe-api";
import { useOnboarding } from "@/contexts/OnboardingContext";
import {
	type NormalizedPropertyRecord,
	type NearbyPropertiesResponse,
} from "@/services/properties-api";

interface PlaygroundScreenProps {
	initialData?: NearbyPropertiesResponse;
}

export default function PlaygroundScreen({
	initialData,
}: PlaygroundScreenProps) {
	const { isLoading: isAuthLoading } = useOnboarding();
	const searchParams = useSearchParams();
	const router = useRouter();
	const DEFAULT_COUNTY_STATE = "Atlantic, New Jersey";
	const [selectedAddress, setSelectedAddress] = useState<AddressData | null>(
		null
	);
	const [selectedCountyState, setSelectedCountyState] =
		useState<string>(DEFAULT_COUNTY_STATE);
	const [isLoading, setIsLoading] = useState(false);
	const [isInitialized, setIsInitialized] = useState(false);
	const [isUpgrading, setIsUpgrading] = useState(false);

	// Check for address or county/state in URL params (from landing page search)
	// If no params, use default county
	useEffect(() => {
		const lat = searchParams.get("lat");
		const lng = searchParams.get("lng");
		const address = searchParams.get("address");
		const countyState = searchParams.get("countyState");

		if (lat && lng && address) {
			setSelectedAddress({
				formattedAddress: decodeURIComponent(address),
				latitude: parseFloat(lat),
				longitude: parseFloat(lng),
			});
			setSelectedCountyState(""); // Clear county/state when address is selected
			setIsInitialized(true);
		} else if (countyState) {
			setSelectedCountyState(decodeURIComponent(countyState));
			setSelectedAddress(null); // Clear address when county/state is selected
			setIsInitialized(true);
		} else if (!isInitialized) {
			// Set default county if no params and not yet initialized
			setSelectedCountyState(DEFAULT_COUNTY_STATE);
			setSelectedAddress(null);
			// Update URL with default county
			const params = new URLSearchParams();
			params.set("countyState", encodeURIComponent(DEFAULT_COUNTY_STATE));
			router.replace(`/playground?${params.toString()}`);
			setIsInitialized(true);
		}
	}, [searchParams, isInitialized, router]);

	const handleAddressSelect = (address: AddressData) => {
		setSelectedAddress(address);
		setSelectedCountyState(""); // Clear county/state when address is selected
		// Update URL params
		const params = new URLSearchParams();
		if (address.latitude && address.longitude) {
			params.set("lat", address.latitude.toString());
			params.set("lng", address.longitude.toString());
			params.set("address", encodeURIComponent(address.formattedAddress));
			router.replace(`/playground?${params.toString()}`);
		}
	};

	const handleCountyStateChange = (value: string) => {
		setSelectedCountyState(value);
		setSelectedAddress(null); // Clear address when county/state is selected
		// Update URL params
		const params = new URLSearchParams();
		if (value) {
			params.set("countyState", encodeURIComponent(value));
			router.replace(`/playground?${params.toString()}`);
		} else {
			router.replace("/playground");
		}
	};

	const handleSignUpClick = async () => {
		try {
			setIsUpgrading(true);
			const { url } =
				await createCheckoutSessionWithAuth(STRIPE_PRO_PRICE_ID);
			if (url) {
				window.location.href = url;
			}
		} catch (error) {
			console.error("Failed to start checkout:", error);
			// Only reset if we didn't redirect
			if (
				error instanceof Error &&
				error.message !== "Redirecting to login"
			) {
				setIsUpgrading(false);
			}
		}
	};

	const hasSearchCriteria = selectedAddress || selectedCountyState;

	if (isAuthLoading) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader2 className="w-8 h-8 animate-spin text-[#16251c]" />
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-white" data-testid="playground-container">
			{/* Search Bar and County Filter */}
			<div
				className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10"
				data-testid="playground-header"
			>
				<div className="max-w-[1800px] mx-auto">
					<div className="flex gap-4 items-start">
						<div style={{ width: "40%" }}>
							<SearchBar
								onAddressSelect={handleAddressSelect}
								isLoading={isLoading}
								fullWidth={true}
								value={selectedAddress?.formattedAddress || ""}
							/>
						</div>
						<div className="w-[300px] flex-shrink-0">
							<CountyStateFilter
								value={selectedCountyState}
								onChange={handleCountyStateChange}
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Playground Filters & Results */}
			<div data-testid="playground-filters">
				{/* Playground Table */}
				{hasSearchCriteria ? (
					<div data-testid="playground-results">
						<PlaygroundTable
							address={selectedAddress || undefined}
							countyState={selectedCountyState || undefined}
							onSignUpClick={handleSignUpClick}
							onLoadingChange={setIsLoading}
							isUpgrading={isUpgrading}
							initialData={initialData}
						/>
					</div>
				) : (
					<div
						className="flex items-center justify-center min-h-[60vh] pb-20"
						data-testid="playground-results"
					>
						<div className="text-center">
							<h2 className="text-2xl font-bold text-gray-900 mb-2">
								Search for Properties
							</h2>
							<p className="text-gray-600">
								Enter an address or select a county above to explore
								sample properties
							</p>
						</div>
					</div>
				)}
			</div>

			{/* Bottom Navigation Bar */}
			<PlaygroundBottomNav />
		</div>
	);
}
